import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { AzulSkeleton } from '@the-ksquare-group/azul-ds';
import { SearchBar } from 'components/SearchBar';
import UserTable from 'pages/SettingsPage/components/UserManagementSettings/components/UserTable/UserTable';
import React, { FC, useEffect, useState } from 'react';
import { QueryBuilder } from 'utils/formatQueryParams';
import { selectCurrentUser } from 'features/auth/authSlice';
import styles from './UserManagementSettings.module.css';
import { useAppSelector } from 'app/hooks';
import { useGetUsersQuery } from 'features/user/userApi';
import { openInNewTab } from 'utils/openInNewTab';
import { UserRoles } from 'features/auth/authTypes';

export interface UserManagementSettingsProps {
  text: string;
}

const UserManagementSettings: FC<UserManagementSettingsProps> = ({ text }) => {
  const selector = useAppSelector;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [offset, setOffset] = useState(0);
  const [searchUser, setSearchUser] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const itemsPerPage = 10;
  const user = selector(state => selectCurrentUser(state));
  const isAdmin = user ? user.role === 'admin' : false;
  const salesforceBaseUrl = selector(state => state.auth.salesforceBaseUrl);

  const builder = new QueryBuilder();
  const queryString = builder
    .setQueryWhere({ field: 'name', operator: '$substring', value: searchUser })
    .setQueryWhere({
      field: 'role',
      operator: '$eq',
      value: roleFilter,
    })
    .setQueryLimit({ value: itemsPerPage })
    .setQueryOffset({ value: offset })
    .build();

  const { data: dataUsers, isFetching: isFetchingUsers } = useGetUsersQuery(queryString);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleSearch = (query: string) => {
    setSearchUser(query);
  };

  const handleRoleFilter = (event: SelectChangeEvent<string>, _child: React.ReactNode) => {
    setRoleFilter(event.target.value);
  };

  useEffect(() => {
    if (dataUsers) {
      const totalUsersCount = dataUsers.count || 0;
      const total = Math.ceil(totalUsersCount / itemsPerPage);
      setTotalPages(total);
    }
  }, [dataUsers]);

  useEffect(() => {
    const newOffset = (currentPage - 1) * itemsPerPage;
    setOffset(newOffset);
  }, [currentPage, itemsPerPage]);

  const handleCreateUser = () => {
    salesforceBaseUrl && openInNewTab(`${salesforceBaseUrl}/lightning/o/User/new`);
  };
  return (
    <Box p='32px'>
      <Box display='flex' justifyContent='space-between' mb='24px'>
        <Box alignItems='center' columnGap='16px' display='flex'>
          <Typography className={styles.userManagementTitle} variant='h3'>
            {text}
          </Typography>
          <Chip className={styles.userRoleChip} label={isAdmin ? 'Admin' : 'Member'} />
        </Box>
        {isAdmin && salesforceBaseUrl && (
          <Button startIcon={<AddIcon />} variant='contained' onClick={handleCreateUser}>
            Create Users
          </Button>
        )}
      </Box>
      <Grid container mb='24px' spacing='24px'>
        <Grid item sm={8} xs={12}>
          <SearchBar placeholder='Search User' value={searchUser} onSearch={handleSearch} />
        </Grid>
        <Grid item sm={4} xs={12}>
          <FormControl fullWidth>
            <InputLabel
              classes={{ shrink: styles.selectLabelShrink }}
              id='user-role-filter-label'
              sx={{ transform: 'translate(14px, 16px) scale(1)' }}
            >
              User Role
            </InputLabel>
            <Select
              labelId='user-role-filter-label'
              className={styles.userRoleFilterSelect}
              id='user-role-filter-select'
              label='User Role'
              value={roleFilter}
              onChange={handleRoleFilter}
              IconComponent={KeyboardArrowDownIcon}
            >
              <MenuItem value={UserRoles.ADMIN}>Admin</MenuItem>
              <MenuItem value={UserRoles.EDITOR}>Editor</MenuItem>
              <MenuItem value={UserRoles.PDF_ONLY}>PDF Only</MenuItem>
              <MenuItem value={''}>All</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Paper className={styles.userTableWrapper}>
        {isFetchingUsers ? (
          <Box paddingLeft={'24px'}>
            <AzulSkeleton width={140} height={24} variant='text' />
          </Box>
        ) : (
          <span className={styles.showingInfo}>
            {dataUsers !== undefined && dataUsers.data.length
              ? `Showing 1-${dataUsers.data.length} of ${dataUsers.count} users`
              : 'Showing 0 users'}
          </span>
        )}
        <Box className={styles.tableContainer}>
          {isFetchingUsers ? (
            <Box padding='24px'>
              <AzulSkeleton height={240} variant='rounded' />
            </Box>
          ) : (
            <>
              <UserTable data={dataUsers !== undefined && dataUsers.data ? dataUsers.data : []} />
            </>
          )}
        </Box>
        <Box className={styles.paginationContainer}>
          {dataUsers !== undefined && dataUsers.count > 10 && (
            <Pagination
              color='primary'
              count={totalPages}
              page={currentPage}
              shape='rounded'
              showFirstButton
              showLastButton
              onChange={handlePageChange}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default UserManagementSettings;
